import { ProductItemStatus } from "../enums/productItems";
import { SellingMode } from "../enums/sellingMode";
import { ProductItem, ProductInventoryStats } from "../types/products";
import { PRODUCT_INVENTORY_STATUS_ORDER } from "../constants/productInventoryStatusConfig";
import {
  partialSoldQuantity,
  supportsPartialSoldTracking
} from "./productItemInventoryFilter";

const createEmptyByStatus = (): Record<ProductItemStatus, number> =>
  PRODUCT_INVENTORY_STATUS_ORDER.reduce(
    (acc, status) => {
      acc[status] = 0;
      return acc;
    },
    {} as Record<ProductItemStatus, number>
  );

/** Quantity attributed to a status bucket for terminal / non-stock states. */
const quantityForItemInStatus = (item: ProductItem): number => {
  switch (item.state) {
    case ProductItemStatus.IN_STOCK:
      return item.remainingQuantity ?? 0;
    case ProductItemStatus.RESERVED:
      return item.reservedQuantity ?? 0;
    case ProductItemStatus.SOLD:
    case ProductItemStatus.CREATED:
    case ProductItemStatus.DAMAGED:
    case ProductItemStatus.EXPIRED:
    case ProductItemStatus.LOST:
    case ProductItemStatus.CONSUMED:
      return item.totalQuantity ?? 0;
    default:
      return 0;
  }
};

export interface AggregateProductInventoryStatsOptions {
  sellingMode: SellingMode;
  isPartialSellingAllowed?: boolean;
}

/** Aggregates inventory metrics across all product items for the current product. */
export const aggregateProductInventoryStats = (
  items: ProductItem[],
  {
    sellingMode,
    isPartialSellingAllowed = false
  }: AggregateProductInventoryStatsOptions
): ProductInventoryStats => {
  const byStatus = createEmptyByStatus();
  const trackPartialSold = supportsPartialSoldTracking(
    sellingMode,
    isPartialSellingAllowed
  );

  items.forEach((item) => {
    const status = item.state as ProductItemStatus;

    if (status === ProductItemStatus.IN_STOCK) {
      byStatus[ProductItemStatus.IN_STOCK] += item.remainingQuantity ?? 0;
      byStatus[ProductItemStatus.RESERVED] += item.reservedQuantity ?? 0;
    } else if (status in byStatus) {
      byStatus[status] += quantityForItemInStatus(item);
    }

    if (
      trackPartialSold &&
      (status === ProductItemStatus.IN_STOCK ||
        status === ProductItemStatus.RESERVED)
    ) {
      byStatus[ProductItemStatus.SOLD] += partialSoldQuantity(item);
    }
  });

  return {
    /** Count of product item rows (each row = one trackable item / QR code). */
    totalItems: items.length,
    byStatus
  };
};
